import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../services/api';

const UCloudTokenManager = ({ token, onUnauthorized }) => {
  const [tokenInfo, setTokenInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showRefreshModal, setShowRefreshModal] = useState(false);
  const [refreshCredentials, setRefreshCredentials] = useState({
    username: '',
    password: ''
  });
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [refreshError, setRefreshError] = useState('');

  // 获取当前token信息
  const fetchTokenInfo = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/scripts/ucloud-token`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        onUnauthorized();
        return;
      }

      if (!response.ok) {
        throw new Error('获取token信息失败');
      }

      const data = await response.json();
      setTokenInfo(data);
    } catch (error) {
      console.error('获取token信息失败:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTokenInfo();
  }, []);

  // 手动刷新token
  const handleRefreshToken = async () => {
    setRefreshLoading(true);
    setRefreshError('');

    try {
      const response = await fetch(`${API_BASE_URL}/scripts/ucloud-token/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(refreshCredentials)
      });

      if (response.status === 401) {
        onUnauthorized();
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || '刷新token失败');
      }

      const data = await response.json();
      setTokenInfo({
        token: data.token,
        expires_at: data.expires_at
      });

      // 关闭模态框并重置表单
      setShowRefreshModal(false);
      setRefreshCredentials({ username: '', password: '' });
    } catch (error) {
      setRefreshError(error.message);
    } finally {
      setRefreshLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setRefreshCredentials(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRefreshSubmit = (e) => {
    e.preventDefault();
    handleRefreshToken();
  };

  // 格式化过期时间
  const formatExpiration = (expiresAt) => {
    if (!expiresAt) return '未知';

    const date = new Date(expiresAt);
    return date.toLocaleString('zh-CN');
  };

  // 检查token是否过期
  const isTokenExpired = (expiresAt) => {
    if (!expiresAt) return true;
    return new Date(expiresAt) <= new Date();
  };

  return (
    <div className="ucloud-token-manager">
      {/* 手动刷新Token模态框 */}
      {showRefreshModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>手动刷新UCLOUD Token</h3>
              <button
                className="modal-close"
                onClick={() => {
                  setShowRefreshModal(false);
                  setRefreshCredentials({ username: '', password: '' });
                  setRefreshError('');
                }}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleRefreshSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="username">用户名:</label>
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={refreshCredentials.username}
                    onChange={handleInputChange}
                    required
                    placeholder="请输入UCLOUD用户名"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="password">密码:</label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={refreshCredentials.password}
                    onChange={handleInputChange}
                    required
                    placeholder="请输入UCLOUD密码"
                  />
                </div>

                {refreshError && (
                  <div className="error-message">
                    <div className="error-content">
                      <span className="error-icon">⚠️</span>
                      <span className="error-text">{refreshError}</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowRefreshModal(false);
                    setRefreshCredentials({ username: '', password: '' });
                    setRefreshError('');
                  }}
                >
                  取消
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={refreshLoading}
                >
                  {refreshLoading ? (
                    <>
                      <span className="spinner"></span> 刷新中...
                    </>
                  ) : '刷新Token'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <div className="card">
        <div className="card-header">
          <h2>UCLOUD Token 管理</h2>
          <div className="card-actions">
            <button
              className="btn btn-primary"
              onClick={fetchTokenInfo}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner"></span> 加载中...
                </>
              ) : '刷新信息'}
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => setShowRefreshModal(true)}
            >
              手动刷新Token
            </button>
          </div>
        </div>

        <div className="card-body">
          {tokenInfo ? (
            <div className="token-info">
              <div className="info-item">
                <label>Token:</label>
                <div className="token-value">
                  <code className="token-code">{tokenInfo.token}</code>
                </div>
              </div>
              <div className="info-item">
                <label>过期时间:</label>
                <span className={`expiration-time ${isTokenExpired(tokenInfo.expires_at) ? 'expired' : 'valid'}`}>
                  {formatExpiration(tokenInfo.expires_at)}
                </span>
              </div>
              <div className="info-item">
                <label>状态:</label>
                <span className={`status-badge ${isTokenExpired(tokenInfo.expires_at) ? 'status-error' : 'status-success'}`}>
                  {isTokenExpired(tokenInfo.expires_at) ? '已过期' : '有效'}
                </span>
              </div>
            </div>
          ) : (
            <div className="empty-state">
              <p>暂无token信息</p>
            </div>
          )}
        </div>
      </div>


    </div>
  );
};

export default UCloudTokenManager;